import {
  CUSTOM_EVENTS,
  Constants,
  IFP_TIERED_NETWORK,
  LineOfBusiness,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../common/Utils';
import { ProviderType } from '../../models/ProviderDetails';
import { ChipState } from '../../store/useChipStore/chipStore';
import {
  GuestAcquisitionRequestData,
  ProviderDetailsMaestroResponse,
} from '../../store/useGuestAcquisitionStore/guestAcquisitionStore';
import { DateTime } from '../../utils/DateTime';
import { getProviderDetailsRouteUrl } from '../../utils/providerDetails.utils';
import { getLanguage } from '../ProviderSearch/context/Internationalization/helpers';
import { toastErrorStyle } from './GuestMultiplePlan.style';
import { isLawwGuest } from './utils';

export const getLobForPortalData = (lob: string): string => {
  switch (lob) {
    case 'EI':
      return 'E&I';
    case 'MR':
      return 'M&R';
    case 'CS':
      return 'C&S';
    case 'IFP':
      return 'IFP';
    case 'SHOP':
      return 'SHOP';
    default:
      return '';
  }
};

export const getLobFromPortalLOB = (lob: string): string => {
  switch (lob) {
    case 'E&I':
      return 'EI';
    case 'M&R':
      return 'MR';
    case 'C&S':
      return 'CS';
    case 'IFP':
      return 'IFP';
    case 'SHOP':
      return 'SHOP';
    default:
      return '';
  }
};

export const getReciprocityId = (
  lob: string,
  allConfigs?: Array<{ key: string; value?: string }>
): string => {
  const shortLob = getLobFromPortalLOB(lob);
  return (
    (shortLob &&
      allConfigs?.find((row) => row.key === `${shortLob}_NETWORK_ID`)?.value) ||
    ''
  );
};

export const getPopulation = (
  coverageType: string,
  lob: string,
  searchDirectory: string
): string => {
  if (lob === 'CS') return 'Medicaid';
  if (lob === 'IFP') return 'IFP';
  if (lob === 'SHOP' && searchDirectory === 'IFP') return 'IFP';
  if (coverageType === 'M' && lob === 'MR') return 'COSMOS';
  if (coverageType === 'M' && lob === 'EI') {
    switch (searchDirectory) {
      case Constants.MEMBER_TYPES.USP:
        return Constants.MEMBER_TYPES.USP;
      case Constants.MEMBER_TYPES.SIERRA:
        return Constants.MEMBER_TYPES.SIERRA;
      default:
        return Constants.MEMBER_TYPES.UNET;
    }
  }
  return '';
};

export const buildPortalData = (
  selectedPlan: any,
  lob: string,
  coverageType: string,
  stateCode: string,
  planYear?: string,
  signInUrl?: string,
  planCategory?: string,
  planName?: string,
  policyNumber?: string,
  allConfigs?: Array<{ key: string; value?: string }>,
  pvrcode = '',
  clusterKeyFromDeeplink = ''
): any => {
  const { planName: selectedPlanName, guestPlanIdentifier } = selectedPlan;
  const year =
    planYear && selectedPlan.years?.length > 1
      ? selectedPlan.years.find((year) => year.planYear === planYear)
      : selectedPlan.years?.[0] ?? {};

  const {
    reciprocityId: reciprocityIdFromYear,
    clusterKey: clusterKeyFromYear,
  } = year;
  const reciprocityId = reciprocityIdFromYear || selectedPlan.reciprocityId;
  const clusterKey =
    clusterKeyFromDeeplink || clusterKeyFromYear || selectedPlan.clusterKey;

  const searchDirectory = selectedPlan?.searchDirectory || null;
  const population = getPopulation(coverageType, lob, searchDirectory);
  const lobForGuest = getLobForPortalData(lob);
  const currentYear =
    DateTime.getCurrentDateTimeWithCentralOffset().getFullYear();
  const selectedYear = planYear ?? currentYear;
  const coverageEffStartDate = `${selectedYear}-01-01`;
  const coverageEffEndDate = `${selectedYear}-12-31`;

  let isIFPTieredPlan: boolean | undefined = undefined;
  if (lobForGuest === 'IFP') {
    isIFPTieredPlan = reciprocityId === IFP_TIERED_NETWORK;
  }

  const rpk = lobForGuest === 'M&R' ? selectedPlan?.planIdentifier : '';

  const finalReciprocityId =
    (coverageType === 'B' && isLawwGuest()
      ? getReciprocityId(lobForGuest, allConfigs)
      : null) ||
    reciprocityId ||
    '52';

  return {
    lineOfBusiness: lobForGuest,
    reciprocityId: finalReciprocityId,
    population,
    coverageTypeCode: coverageType || 'M',
    coverageEffStartDate,
    coverageEffEndDate,
    stateCode,
    planName: planName || selectedPlanName,
    planYear: selectedYear,
    signInUrl,
    searchDirectory,
    planCategory,
    guestPlanIdentifier,
    isIFPTieredPlan,
    rulesPackageKey: clusterKey || rpk || '',
    policyNumber,
    pvrcode,
  };
};

export const LOB_MAPPING = {
  EI: [
    { coverage: 'M', locationRequirement: 'n/a' },
    { coverage: 'B', locationRequirement: 'n/a' },
    { coverage: 'D', locationRequirement: 'state' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  MR: [
    { coverage: 'M', locationRequirement: 'county' },
    { coverage: 'B', locationRequirement: 'county' },
    { coverage: 'D', locationRequirement: 'state' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  CS: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'state' },
  ],
  IFP: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'n/a' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
  SHOP: [
    { coverage: 'M', locationRequirement: 'state' },
    { coverage: 'B', locationRequirement: 'state' },
    { coverage: 'D', locationRequirement: 'n/a' },
    { coverage: 'V', locationRequirement: 'n/a' },
  ],
};

export const getLocationRequirement = (
  lob: string,
  coverageType: string
): string => {
  const lobConfig = LOB_MAPPING[lob];

  if (!lobConfig) {
    throw new Error(`LOB ${lobConfig} is not defined in LOB_MAPPING.`);
  }

  const coverageConfig = lobConfig.find(
    (item) => item.coverage === coverageType
  );

  if (!coverageConfig) {
    throw new Error(
      `Coverage type ${coverageType} is not defined for LOB ${lobConfig}.`
    );
  }

  return coverageConfig.locationRequirement;
};

export const getChipValue = (coverageType: string): string => {
  let chipValue = 'All';
  if (coverageType === 'B') {
    chipValue = 'Behavioral%20Health';
  } else if (coverageType === 'D') {
    chipValue = 'Dental';
  } else if (coverageType === 'V') {
    chipValue = 'Vision';
  }
  return chipValue;
};

const PAGE_NAME_MAPPING: {
  [key: string]: string | { location: string; results: string };
} = {
  M: { location: 'medical', results: 'medical search results' },
  B: {
    location: 'behavioral health',
    results: 'behavioral health search results',
  },
  D: { location: 'dental', results: 'dental search results' },
  V: { location: 'vision', results: 'vision search results' },
};

export const getGuestLocationPageName = (coverage: string): string => {
  const mapping = PAGE_NAME_MAPPING[coverage];
  if (typeof mapping === 'object') {
    return mapping.location;
  }
  return 'medical';
};

export const getGuestResultsPageName = (coverage: string): string => {
  const mapping = PAGE_NAME_MAPPING[coverage];
  if (typeof mapping === 'object') {
    return mapping.results;
  }
  return 'medical search results';
};

export const toSlug = (input: string | undefined | null): string =>
  input ? input.replace(/\s+/g, '-').toLowerCase() : '';

export const shouldSkipPlanSelection = (availablePlans: any[]): boolean => {
  // Skip plan selection only if there is one plan with one year available
  if (availablePlans?.length !== 1) return false;
  const planYears = availablePlans?.[0]?.planDetails?.[0]?.years?.length || 0;
  return planYears === 1;
};

export const filterUniquePlanNames = <T extends { planName?: string }>(
  allPlanNames: T[] | undefined,
  uniquePlanNames: Set<string>
): T[] => {
  if (!allPlanNames) return [];

  return allPlanNames.filter((name) => {
    const planName = name?.planName;
    if (planName && !uniquePlanNames.has(planName)) {
      uniquePlanNames.add(planName);
      return true;
    }
    return false;
  });
};

export function getCoverageTypeString(coverageType: string): string {
  switch (coverageType) {
    case 'M':
      return 'medical';
    case 'B':
      return 'behavioral';
    case 'D':
      return 'dental';
    case 'V':
      return 'vision';
    default:
      return '';
  }
}

export function extractZipCodeFromPlaceName(placeName: string): string | null {
  if (!placeName) return null;
  // Match a 5-digit zipcode number at the end of a comma-separated segment
  const match = placeName.match(/\b(\d{5})(?:-[0-9]{4})?\b/);
  return match ? match[1] : null;
}

export function getYearIndex(
  years: Array<{ planYear: string }>,
  title: string
): number {
  if (!Array.isArray(years) || !title) return 0;
  const idx = years.findIndex((year) => year.planYear === title);
  return idx === -1 ? 0 : idx;
}

/**
 * Determines if a plan should be treated as an external link.
 * Returns true if any year in the plan has groupDriven=true or a non-null guestPath.
 * When ENABLE_GROUP_DRIVEN feature flag is true and lob is 'MR', groupDriven is not checked,
 * and the plan is treated as a regular PSX plan (only guestPath triggers external link).
 */
export function IsExternallink(
  plan?: {
    years?: Array<{ groupDriven?: boolean; guestPath?: string | null }>;
  },
  lob?: string,
  featureFlags?: { key: string; active: boolean }[]
): boolean {
  if (!plan?.years || !Array.isArray(plan.years)) return false;

  const isGroupDrivenEnabled = getFeatureFlag(
    featureFlags || [],
    ConstantsLagoon.GUEST_PLAN_SELECTION.ENABLE_GROUP_DRIVEN
  );

  // When ENABLE_GROUP_DRIVEN is true and lob is MR, skip groupDriven check
  // Plans should be treated as regular plans within PSX
  if (isGroupDrivenEnabled && lob === LineOfBusiness.MR) {
    return plan.years.some((year) => !!year.guestPath);
  }

  return plan.years.some((year) => year.groupDriven || !!year.guestPath);
}

export function setGuestPlanSelectionData({
  selectedPlan,
  planYear = '',
  coverageType = 'M',
  lob,
  stateCode = '',
  isDual = false,
  planCategory = '',
  planName = '',
  policyNumber = '',
  allConfigs = undefined,
  pvrcode = '',
  clusterKeyFromDeeplink = '',
}) {
  let selectedPlanData = selectedPlan;
  if (planName) {
    selectedPlanData = {
      ...selectedPlan,
      planName,
    };
  }

  sessionStorage.setItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA,
    JSON.stringify({
      ...buildPortalData(
        selectedPlanData,
        lob,
        coverageType,
        stateCode,
        planYear || selectedPlanData?.years?.[0].planYear,
        undefined,
        planCategory,
        planName,
        policyNumber,
        allConfigs,
        pvrcode,
        clusterKeyFromDeeplink
      ),
      isDual,
      selectedPlan: selectedPlanData,
    })
  );
}

export function getGuestPlanSelectionData() {
  const planSelectionData = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.GUEST_PLAN_SELECTION_DATA
  );
  try {
    return planSelectionData ? JSON.parse(planSelectionData) : {};
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      'Error parsing guest plan selection data from sessionStorage',
      e
    );
    return {};
  }
}

export async function handleGuestPlanNavigation({
  getFpcGuestToken,
  selectedPlan,
  planYear,
  fipscode,
  FPC_BASE_URL,
  zipcode,
  chipValue,
  coverageType,
  navigate,
  lob,
  stateCode,
  longitude,
  latitude,
  signInUrl,
  toast,
  toastMessageForFpcTokenError = '',
  isDual = false,
  planCategory = '',
  guestPlanSelectorStoreData,
  isGroupDrivenEnabled = false,
  allConfigs = undefined,
  getUserLocation,
  userLocation,
}) {
  const yearIndex = getYearIndex(selectedPlan?.years, planYear);
  const yearObj = selectedPlan?.years?.[yearIndex];
  const groupDrivenCheck =
    isGroupDrivenEnabled && lob === LineOfBusiness.MR
      ? false
      : yearObj?.groupDriven;

  if (groupDrivenCheck) {
    let urlZipCode = zipcode;
    const language = getLanguage()?.code;
    const planIdentifier =
      typeof selectedPlan?.planIdentifier === 'string'
        ? coverageType === 'B'
          ? selectedPlan.planIdentifier
          : selectedPlan.planIdentifier.replace(/-/g, '')
        : '';
    if (!urlZipCode) {
      await getUserLocation({
        variables: {
          longitude,
          latitude,
        },
      });
      const placeName =
        userLocation?.data?.userLocation?.features?.[0]?.place_name;
      urlZipCode = extractZipCodeFromPlaceName(placeName) ?? '';
    }
    const token = await getFpcGuestToken({
      variables: {
        planName: selectedPlan?.planName,
        delsys: planIdentifier,
        coverageType: getCoverageTypeString(coverageType),
        language: language || 'en',
        clusterKey: yearObj?.clusterKey || '',
        fipsCode: fipscode,
        partnerId: 'uhc',
      },
    }).then(
      async (response: { data: { getFpcGuestToken: { token: string } } }) =>
        response?.data?.getFpcGuestToken?.token || ''
    );
    if (!token) {
      toast.show({
        message: toastMessageForFpcTokenError,
        autoClose: 30000,
        css: toastErrorStyle,
      });
      return;
    }
    window.open(
      `${FPC_BASE_URL}/guest/${token}?planYear=${planYear}&zipCode=${urlZipCode}`,
      '_blank'
    );
    return;
  }
  if (yearObj?.guestPath) {
    const url = yearObj.guestPath;
    window.open(url, '_blank');
    return;
  }

  const {
    planName,
    policyNumber,
    search,
    source,
    pvrcode,
    clusterKey: clusterKeyFromDeeplink,
  } = guestPlanSelectorStoreData || {};

  setGuestPlanSelectionData({
    selectedPlan,
    planYear,
    coverageType,
    lob,
    stateCode,
    isDual,
    planCategory,
    planName,
    policyNumber,
    allConfigs,
    pvrcode,
    clusterKeyFromDeeplink,
  });

  const navigationPath =
    source === 'deeplink' && search
      ? `/browse/results/?coverageType=${coverageType}&search=${encodeURIComponent(
          search
        )}&psx-source=deeplink`
      : `/browse?chipValue=${chipValue}&coverageType=${coverageType}`;

  navigate(navigationPath, {
    state: buildPortalData(
      selectedPlan,
      lob,
      coverageType,
      stateCode || '',
      planYear,
      signInUrl,
      planCategory,
      planName,
      policyNumber,
      allConfigs,
      pvrcode,
      clusterKeyFromDeeplink
    ),
  });
}

export const redirectToGuestSignIn = (
  // two supported call signatures:
  // 1) (signInUrl: string, locale?: string)
  // 2) (portalBasePath: string, signInUrl: string, chipStore: ChipState, providerId: string, selectedId: string, showDirection: boolean, isTieredDataCard: boolean, sectionType: string, choosePCPFlag: boolean, locale?: string)
  ...args: any[]
): void => {
  // If called with more than 2 args, treat as scheduling-callback style and build callback URL
  if (args.length > 2) {
    const [
      portalBasePath,
      signInUrl,
      chipStore,
      providerId = '',
      selectedId,
      showDirection,
      isTieredDataCard = false,
      sectionType,
      choosePCPFlag = false,
      locale = 'en-US',
    ] = args;

    const providerDetailsRoute = getProviderDetailsRouteUrl(
      chipStore,
      providerId,
      selectedId,
      showDirection,
      isTieredDataCard,
      sectionType,
      choosePCPFlag
    );

    const targetPath = `&initiateDocasapSession=true`;
    const callBackUrl = `${portalBasePath}${providerDetailsRoute}${targetPath}`;
    const encodedCallBackUrl = encodeURIComponent(
      `${providerDetailsRoute}${targetPath}`
    );

    const URL = signInUrl
      ?.replace(/ENCODED_LANDING_PAGE/g, encodedCallBackUrl)
      .replace(/LANDING_PAGE/g, callBackUrl)
      .replace(/LOCALE_VALUE/g, locale);
    window.location.href = URL;
    return;
  }

  // Fallback to simple sign-in redirect: (signInUrl, locale?)
  const [signInUrl, locale = 'en-US'] = args as [string, string?];
  const URL = signInUrl
    ?.replace(/ENCODED_LANDING_PAGE/g, '?')
    .replace(/LOCALE_VALUE/g, locale);
  window.location.href = URL;
};

export const redirectToGuestSignInSchedulingCallback = (
  portalBasePath: string,
  signInUrl: string,
  chipStore: ChipState,
  providerId: string = '',
  selectedId: string,
  showDirection: boolean,
  isTieredDataCard: boolean = false,
  sectionType: string,
  choosePCPFlag: boolean = false,
  locale: string = 'en-US'
): void => {
  const providerDetailsRoute = getProviderDetailsRouteUrl(
    chipStore,
    providerId,
    selectedId,
    showDirection,
    isTieredDataCard,
    sectionType,
    choosePCPFlag
  );

  const targetPath = `&initiateDocasapSession=true`;
  const callBackUrl = `${portalBasePath}${providerDetailsRoute}${targetPath}`;
  const encodedCallBackUrl = encodeURIComponent(
    `${providerDetailsRoute}${targetPath}`
  );

  const URL = signInUrl
    ?.replace(/ENCODED_LANDING_PAGE/g, encodedCallBackUrl)
    .replace(/LANDING_PAGE/g, callBackUrl)
    .replace(/LOCALE_VALUE/g, locale);
  window.location.href = URL;
};

export const buildGuestSignInUrl = (
  signInUrl: string,
  locale?: string,
  coverageType?: string,
  chipValue?: string
) => {
  const urlParams = `?${chipValue ? `chipValue=${chipValue}` : ''}${
    coverageType ? `&coverageType=${coverageType}` : ''
  }`;
  const encodedCallBackUrl = encodeURIComponent(urlParams);

  const URL = signInUrl
    ?.replace(/ENCODED_LANDING_PAGE/g, encodedCallBackUrl)
    .replace(/LOCALE_VALUE/g, locale || 'en-US');

  return URL;
};

export const getAvailablePlans = (planDefinitions: any) => {
  const uniqueCategoryPlanNames = new Set<string>();
  const planCategoryList = planDefinitions?.planCategories
    ? planDefinitions?.planCategories
        ?.map((category) => {
          const uniquePlansInCategory = new Set<string>();
          const filteredPlanDetails = filterUniquePlanNames(
            category?.planDetails,
            uniquePlansInCategory
          );

          return {
            planCategory: category?.planCategory,
            planDetails: filteredPlanDetails,
          };
        })
        .filter((category) => {
          if (category.planDetails && category.planDetails.length > 0) {
            const planName = category.planDetails[0]?.planName;
            if (planName && !uniqueCategoryPlanNames.has(planName)) {
              uniqueCategoryPlanNames.add(planName);
              return true;
            }
          }
          return false;
        })
    : [];

  const uniqueDetailPlanNames = new Set<string>();
  const filteredPlanDetails = filterUniquePlanNames(
    planDefinitions?.planDetails,
    uniqueDetailPlanNames
  );

  const planDetailsList = filteredPlanDetails.map((plan) => ({
    planDetails: [plan],
  }));

  return [...(planCategoryList || []), ...(planDetailsList || [])];
};

export const filterAvailablePlansByPlanName = (
  combinedPlans: any[] = [],
  planName?: string
) => {
  let filteredPlans = combinedPlans || [];

  if (planName && combinedPlans && combinedPlans?.length > 0) {
    const deeplinkPlanName = planName.trim().toLowerCase();
    const matchingPlans = combinedPlans
      .map((plan) => ({
        ...plan,
        planDetails: (plan?.planDetails || []).filter(
          (planDetail: any) =>
            planDetail?.planName?.trim().toLowerCase() === deeplinkPlanName
        ),
      }))
      .filter((plan) => plan?.planDetails?.length > 0);

    // Only use filtered plans if matches found, otherwise show all plans
    if (matchingPlans?.length > 0) {
      filteredPlans = matchingPlans;
    }
  }

  return filteredPlans;
};

export const filterForLevelFundedPlans = (categories) =>
  categories
    ?.map((category) => {
      // Filter to get only the level funded plans for this category
      const levelFundedPlans = category.planDetails?.filter(
        (plan) => plan?.levelFunded
      );

      // Return a NEW category object with the filtered plans, avoiding mutation of the original data
      return {
        ...category,
        planDetails: levelFundedPlans,
      };
    })
    .filter(
      (category) =>
        // Then, filter out any categories that no longer have plans
        category.planDetails && category.planDetails.length > 0
    );

export const createPostProviderPayload = (
  requestData: GuestAcquisitionRequestData,
  providers: ProviderDetailsMaestroResponse[],
  selectedPlan: any,
  coverageType: string,
  reciprocityId: string
) => ({
  ts: Date.now(),
  txnid: requestData?.transactionId,
  network: reciprocityId,
  networkName: selectedPlan?.planName,
  fipsCode: requestData?.fipsCode,
  zip: requestData?.zip,
  providers: providers?.flatMap((provider) => {
    const locations = provider?.locations;
    if (!locations || locations.length === 0) return [];

    return locations.map((location) => ({
      providerId: provider?.providerId,
      nationalProviderId: provider?.npi,
      providerAddressId: location?.locationId,
      directoryType: coverageType,
      providerAddress: {
        street1: location?.address?.line?.[0],
        city: location?.address?.city,
        state: location?.address?.state,
        zip: location?.address?.postalCode?.slice(0, 5),
        phone: location?.phones?.phone?.[0],
      },
      providerPlans: [
        {
          planDescription: selectedPlan?.planName,
          planCode: reciprocityId,
        },
      ],
      specialties: provider?.specialties,
      pcpId: [location?.pcpId || ''],
      providerType:
        provider?.providerType === ProviderType.PRACTITIONER
          ? 'individual'
          : provider?.providerType,
      providerName:
        provider?.providerType !== ProviderType.PRACTITIONER
          ? {
              name: provider?.providerName,
            }
          : {
              lastName: provider?.lastName,
              firstName: provider?.firstName,
              middleName: provider?.middleName,
              degree: provider?.primaryDegrees?.join(','),
            },
      gender: provider?.gender?.charAt(0).toLowerCase() || 'Unknown',
      acceptingNewPatients: location?.acceptingNewPatients ? 'Y' : 'N',
      additionalLanguages: provider?.languagesSpoken,
      pcpQualifiedId:
        location?.acceptingNewPatients && location?.hasPCPServiceAvailability,
      tin: location?.tin,
      ...(provider?.networkAffiliationData?.networkAffiliationId && {
        affiliation: {
          affiliationId: provider?.networkAffiliationData?.networkAffiliationId,
          pcpId: provider?.networkAffiliationData?.pcpId,
          name: provider?.networkAffiliationData?.networkAffiliationName,
        },
      }),
    }));
  }),
});

export const postProviderInfo = async (
  targetUrl: string,
  payload: ReturnType<typeof createPostProviderPayload>
): Promise<{ success: boolean; html: string | null }> => {
  const params = new URLSearchParams({
    json: JSON.stringify(payload),
  });
  try {
    const response = await fetch(targetUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
    const html = await response.text();
    return { success: response.ok, html };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('PostProviderInfo error:', error);
    return { success: false, html: null };
  }
};

export const getProviderCount = (
  providers: ProviderDetailsMaestroResponse[]
) => {
  if (!providers || providers.length === 0) return 0;
  return providers.reduce((count, provider) => {
    const locations = provider?.locations;
    if (!locations || locations.length === 0) return count;
    return count + locations.length;
  }, 0);
};

export const triggerResponseDataUpdated = (
  newResponseData: ProviderDetailsMaestroResponse[]
) => {
  window.dispatchEvent(
    new CustomEvent(CUSTOM_EVENTS.RESPONSE_DATA_UPDATED, {
      detail: newResponseData,
    })
  );
};
